/**
 * Universal red-flag safety screen + region major-signals.
 *
 * PROVENANCE: Wording and scores ported one-time from ara-health
 *   server/data/questionSets/universal-safety.json (7 universal questions +
 *   follow-up detail questions), region-lumbar-spine.json (cauda-equina
 *   re-screen), region-knee.json (Ottawa rule + locked knee) and
 *   region-shoulder.json (loss of active elevation), on 2026-07-11. One-time
 *   port, no shared code, no ongoing dependency on ara-health.
 *
 * Implements the signed B2 decision record
 *   docs/B2_red_flag_alignment_DECISION_RECORD_signed_2026-07-11.docx
 *   (Dr S Armstrong MCNZ 36701, 2026-07-11), with the record's ONE amendment:
 *   the patient-facing intro (see UNIVERSAL_INTRO) diverges from Ara's — the
 *   "already been seen…" sentence is removed and "seen" -> "managed".
 *
 * Scoring model (option (b), two independent passes — see redFlagChecker.js):
 *   PASS 1 (escalation tier): the 7 universal questions PLUS the lumbar CE
 *     re-screen, on Ara's 0-3 scale, tier = MAX of contributions:
 *     3 -> CRITICAL, 2 -> URGENT, 1 -> CONCERNING, 0 -> none.
 *   Region major-signals for KNEE and SHOULDER do NOT feed the escalation tier
 *     (clarification to signed record §1.2, per clinician direction 2026-07-11:
 *     "with proposed weights" applies to the lumbar CE re-screen only). They
 *     carry Ara's native signal semantics — Ottawa is a sum-based imaging-
 *     decision signal; locked-knee and shoulder loss-of-elevation are boolean
 *     flags — surfaced to the clinician, never raising the patient alert level.
 */

// The ONE approved amendment to Ara's intro (decision record §1.1).
export const UNIVERSAL_INTRO =
  "Before we ask about your injury in detail, we need to check a few things that would change how urgently you should be managed. If any of these apply, we'll ask one or two follow-up questions so the clinician has the right information.";

// The seven universal questions (Yes-triggered). `score` is Ara's 0-3 escalation
// contribution. `followups` render only when the parent answer is "Yes".
export const UNIVERSAL_SCREEN = [
  {
    id: 'neuroSigns',
    score: 3,
    label: "Do you have any of the following in the injured area, or further down the limb (in your fingers, toes, hand, or foot)? Numbness, pins and needles, weakness, parts feeling cold, or skin colour changing (looking pale, bluish, or different).",
    followups: [
      { id: 'neuroFeatures', type: 'checkbox-multiple', label: 'Which of these are happening?', options: ['Numbness', 'Pins and needles', 'Weakness', 'Part feels cold', 'Skin colour change (pale, blue, or different)'] },
      { id: 'neuroLocation', type: 'textarea', label: 'Where exactly are these symptoms?', placeholder: 'e.g. whole hand, just fingers, foot below the ankle' },
    ],
  },
  {
    id: 'feelingUnwell',
    score: 2,
    label: "Have you been feeling really unwell? For example: a fever, the joint feeling hot and swollen, or a recent infection in another part of your body. Or are you taking medicines that lower your immune system (like steroid tablets, chemotherapy, or some arthritis medicines)?",
    followups: [
      { id: 'unwellFeatures', type: 'checkbox-multiple', label: 'Which of these apply?', options: ['Fever', 'Feeling very unwell in general', 'Hot, swollen joint', 'Recent infection somewhere else', 'On medicines that lower the immune system'] },
    ],
  },
  {
    id: 'symptomsGettingWorse',
    score: 3,
    label: "Are any of the following getting worse over hours or days? Weakness, numbness, balance problems, trouble controlling your wee or poo, or numbness between your legs (around the area you sit on).",
    followups: [
      { id: 'worseningFeatures', type: 'checkbox-multiple', label: 'Which of these have been getting worse?', options: ['Weakness', 'Numbness', 'Balance or coordination', 'Trouble controlling wee', 'Trouble controlling poo', 'Numbness between the legs'] },
      { id: 'progressionSpeed', type: 'radio', label: 'How quickly are things changing?', options: ['Within hours', 'Over the last few days', 'Over weeks'] },
    ],
  },
  {
    id: 'nightOrRestPain',
    score: 1,
    label: "Is the pain bad enough to wake you up at night even when you haven't moved, or really bad when you're just resting?",
    followups: [
      { id: 'wakesFromSleep', type: 'radio', label: 'Does the pain wake you from sleep without you having moved?', options: ['Yes', 'No'] },
      { id: 'nightPainDurationWeeks', type: 'text', label: 'How many weeks has the night or rest pain been going on?', placeholder: 'Number of weeks' },
    ],
  },
  {
    id: 'seriousHistory',
    score: 1,
    label: "Do any of these apply to you? You have had cancer in the past; you have lost a lot of weight without trying to; you have weak bones (osteoporosis); you have been on steroid tablets for a long time; or you had a major recent injury.",
    followups: [
      { id: 'seriousHistoryFeatures', type: 'checkbox-multiple', label: 'Which of these apply?', options: ['I have had cancer in the past', 'I have lost a lot of weight without trying to', 'I have weak bones (osteoporosis)', 'I have been on steroid tablets for a long time', 'I had a major injury recently'] },
    ],
  },
  {
    id: 'bloodThinnerOrSteroid',
    score: 0,
    label: "Are you taking blood-thinning medicine (like warfarin, rivaroxaban, dabigatran, aspirin, or clopidogrel), steroid tablets, or do you have a bleeding problem?",
    followups: [
      { id: 'medicationTypes', type: 'checkbox-multiple', label: 'Which of these apply?', options: ['Blood thinner — warfarin, rivaroxaban, dabigatran, or similar', 'Aspirin or clopidogrel', 'Steroid tablets (e.g. prednisone)', 'Known bleeding problem'] },
    ],
  },
  {
    id: 'longstandingVisibleChange',
    score: 1,
    label: "Is there a long-standing change in the way this part of your body looks or works that has never been checked or treated by a doctor? For example, a part that looks bent or out of shape, or a joint that doesn't move properly.",
    followups: [
      { id: 'longstandingChangeDescription', type: 'textarea', label: 'Please describe what looks or works differently.', placeholder: 'e.g. shoulder sits lower than the other side; finger has been bent since an old injury; knee never straightens fully.' },
      { id: 'longstandingChangeDuration', type: 'radio', label: 'Roughly how long has it been like this?', options: ['A few months', 'A year or two', 'Many years', 'Since childhood'] },
    ],
  },
];

// Lumbar cauda-equina re-screen (Yes-triggered). PART OF THE ESCALATION PASS.
export const LUMBAR_CE_RESCREEN = [
  { id: 'saddleNumbness', score: 3, label: "Do you have new numbness or pins and needles between your legs (in the area you sit on, around the bottom or genitals)?" },
  { id: 'bladderChange', score: 3, label: "Do you have new trouble with your wee — for example, struggling to start, not feeling the urge, leaking, or being unable to go?" },
  { id: 'bowelChange', score: 3, label: "Do you have new trouble with your bowels — for example, leaking, constipation, or not feeling when you need to go?" },
  { id: 'legWeaknessProgressing', score: 3, label: "Has weakness in either leg been getting noticeably worse over hours or days?" },
  { id: 'bothLegsSymptoms', score: 2, label: "Do you have numbness, weakness, or pain in both legs at the same time (rather than just one)?" },
];

// KNEE Ottawa rule — sum-based imaging-decision signal. `signalWhen` is the
// answer that counts as a signal (Ottawa fires on "No"; kneeLocked fires on
// "Yes"). NOT an escalation contribution — no `score` on the escalation scale.
export const KNEE_OTTAWA = [
  { id: 'walked4StepsImmediately', signalWhen: 'No', label: "Right after the injury, could you take four steps on the injured leg, even if you were limping?", options: ['Yes', 'No'] },
  { id: 'walked4StepsNow', signalWhen: 'No', label: "Right now, can you take four steps on the injured leg, even if you are limping?", options: ['Yes', 'No'] },
  { id: 'canBend90', signalWhen: 'No', label: "Can you bend your injured knee to a right angle (90 degrees), even if it hurts?", options: ['Yes', 'No'] },
];
export const KNEE_LOCKED = { id: 'kneeLocked', signalWhen: 'Yes', label: "Is the knee physically stuck — meaning you cannot fully straighten it, OR cannot fully bend it, even when you try with effort?", options: ['Yes', 'No'] };

// SHOULDER loss of active elevation — boolean flag (three options; signal on
// "No — cannot get the arm to shoulder height"). NOT an escalation contribution.
export const SHOULDER_ELEVATION = {
  id: 'canActivelyRaiseToShoulder',
  signalValue: 'no',
  label: "Can you actively lift the affected arm out to the side until it is level with your shoulder (parallel to the floor), by yourself, even if it hurts?",
  options: [
    { value: 'yes', label: 'Yes — without help' },
    { value: 'with_assist', label: 'Only if I help it up with my other hand' },
    { value: 'no', label: 'No — cannot get the arm to shoulder height' },
  ],
};

// Which regions carry which major-signals (rendered after the universal screen).
export const REGION_MAJOR_SIGNALS = {
  LUMBAR_SPINE: { ceRescreen: LUMBAR_CE_RESCREEN },
  KNEE: { ottawa: KNEE_OTTAWA, locked: KNEE_LOCKED },
  SHOULDER: { shoulderElevation: SHOULDER_ELEVATION },
};

// All ids that belong to the ESCALATION pass — used by the contamination guard
// to assert the escalation and supplementary passes never share an id.
export const ESCALATION_IDS = new Set([
  ...UNIVERSAL_SCREEN.map((i) => i.id),
  ...LUMBAR_CE_RESCREEN.map((i) => i.id),
]);

// Escalation tier from a Pass-1 max score (0-3).
export function tierFromScore(maxScore) {
  if (maxScore >= 3) return 'critical';
  if (maxScore === 2) return 'urgent';
  if (maxScore === 1) return 'concerning';
  return null;
}

const isYes = (v) => v === 'Yes' || v === true || v === 'yes' || v === 'true';

/**
 * PASS 1 — escalation tier. Universal screen + (for LUMBAR_SPINE) the cauda-
 * equina re-screen only. Max of contributions -> tier. Never includes the
 * knee/shoulder signals.
 */
export function scoreEscalationPass(formData, injuryType) {
  const items = [...UNIVERSAL_SCREEN];
  if (injuryType === 'LUMBAR_SPINE') items.push(...LUMBAR_CE_RESCREEN);

  let maxScore = 0;
  const triggered = [];
  items.forEach((item) => {
    if (isYes(formData[item.id])) {
      triggered.push({ id: item.id, label: item.label, score: item.score });
      if (item.score > maxScore) maxScore = item.score;
    }
  });
  return { maxScore, level: tierFromScore(maxScore), triggered };
}

/**
 * Region clinical signals (imaging-decision / boolean). Surfaced to the
 * clinician; MUST NOT influence the escalation level.
 */
export function computeRegionSignals(formData, injuryType) {
  const signals = {};
  if (injuryType === 'KNEE') {
    const ottawaCount = KNEE_OTTAWA.reduce((n, q) => n + (formData[q.id] === q.signalWhen ? 1 : 0), 0);
    signals.ottawaKnee = {
      count: ottawaCount,
      indicatesXray: ottawaCount >= 1,
      label: ottawaCount >= 1 ? `Ottawa knee rule: ${ottawaCount} positive signal(s) — clinical examination / X-ray decision` : null,
    };
    signals.kneeLocked = formData[KNEE_LOCKED.id] === KNEE_LOCKED.signalWhen;
  }
  if (injuryType === 'SHOULDER') {
    signals.lossOfActiveElevation = formData[SHOULDER_ELEVATION.id] === SHOULDER_ELEVATION.signalValue;
  }
  return signals;
}
